import { useCallback, useEffect, useState } from "react";
import ApiTable from "../../../@ui/ApiTable/ApiTable";
import useBannerMessageService from "../../../hooks/useBannerMessageService";
import useAppSettingsService from "../../../hooks/useAppSettingsService";
import { ApiTableState } from "../../../types/ApiTable/ApiTableState";
import { BannerMessage } from "../../../types/BannerMessage/BannerMessage";
import { ApiTableColumn } from "../../../types/ApiTable/ApiTableColumn";
import { Pagination } from "../../../types/PaginiationResult";
import TableActionButton from "../../../@ui/TableActionButton";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled from "styled-components";
import { TableActionButtonDelete } from "../../../@ui/TableActionButtonDelete";
import DeleteBannerMessageModal from "../../modal/BannerMessage/Delete";
import BannerMessageFormModal from "../../modal/BannerMessage/BannerMessageFormModal/BannerMessageFormModal";
import { BannerMessageRequest } from "../../../types/BannerMessage/BannerMessageCreateRequest";
import ToggleSwitch from "../../../@ui/ToggleSwitch";
import { useMaintenanceModeContext } from "../../../context/MaintenanceMode/MaintenanceModeContext";

const StyledIcon = styled(FontAwesomeIcon)`
  margin-right: 0.5rem;
`;

enum Modal {
  Create,
  Update,
  Delete,
}

const Banner = () => {
  const bannerMessageService = useBannerMessageService();
  const appSettingsService = useAppSettingsService();
  const { isMaintenance, setIsMaintenance } = useMaintenanceModeContext();
  const [modalData, setModalData] = useState<{
    modal: Modal | null;
    bannerMessage: BannerMessage | null;
    apiErrorMessage?: string;
  }>({
    modal: null,
    bannerMessage: null,
    apiErrorMessage: undefined,
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Pagination<BannerMessage>>({
    records: [],
    totalPages: 1,
    page: 1,
  });

  const [requestData, setRequestData] = useState<ApiTableState>({
    pagination: { page: 1, pageSize: 10 },
  });

  useEffect(() => {
    updateTable(
      requestData.pagination?.page ?? 1,
      requestData.pagination?.pageSize ?? 10,
    );
  }, [requestData, bannerMessageService]);

  const updateTable = (page: number, pageSize: number) => {
    setLoading(true);
    bannerMessageService
      .List(requestData.pagination?.page, requestData.pagination?.pageSize)
      .then((result) => {
        setData(result);
        setLoading(false);
      });
  };

  const cols = [
    {
      field: "message",
      label: "Message",
      renderContents: (value: BannerMessage) => <>{value.message}</>,
    },
    {
      field: "displayFrom",
      label: "Display From",
      renderContents: (value: BannerMessage) => (
        <>{value.displayFrom.toFormat("dd/LL/yyyy HH:mm:ss")}</>
      ),
    },
    {
      field: "displayTo",
      label: "Display To",
      renderContents: (value: BannerMessage) => (
        <>{value.displayTo.toUTC().toFormat("dd/LL/yyyy HH:mm:ss")}</>
      ),
    },
    {
      style: { width: "1%" },
      renderContents: (value: BannerMessage) => (
        <>
          <TableActionButton
            onClick={() => {
              setModalData({ modal: Modal.Update, bannerMessage: value });
            }}
            title="Update"
          >
            Update
          </TableActionButton>
        </>
      ),
    },
    {
      style: { width: "1%" },
      renderContents: (value: BannerMessage) => (
        <>
          <TableActionButtonDelete
            onClick={() => {
              setModalData({ modal: Modal.Delete, bannerMessage: value });
            }}
            title="Delete"
          >
            Delete
          </TableActionButtonDelete>
        </>
      ),
    },
  ] as ApiTableColumn[];

  const handleMaintenanceToggle = (value: boolean) => {
    setIsMaintenance(value);
    appSettingsService.createAppSettings({ isMaintenance: value });
  };

  const controls = () => {
    return (
      <div className="w-100 d-flex justify-content-start h-100 align-items-end gap-3">
        <TableActionButton
          onClick={() =>
            setModalData({ modal: Modal.Create, bannerMessage: null })
          }
          title="Create"
        >
          {" "}
          <StyledIcon icon={faPlus} />
          Create
        </TableActionButton>
        <ToggleSwitch
          id="maintenance-mode-toggle"
          label="Maintenance Mode"
          value={isMaintenance ?? false}
          onChange={handleMaintenanceToggle}
        />
      </div>
    );
  };

  const onHide = () => setModalData({ modal: null, bannerMessage: null });

  const onDelete = useCallback(
    (id: number) => {
      bannerMessageService.Delete(id).then(() => {
        updateTable(
          requestData.pagination?.page ?? 1,
          requestData.pagination?.pageSize ?? 10,
        );
      });
    },
    [requestData],
  );

  const onSubmit = (values: BannerMessageRequest) => {
    if (modalData.modal === Modal.Create) {
      bannerMessageService
        .Create(values)
        .then(() => {
          updateTable(
            requestData.pagination?.page ?? 1,
            requestData.pagination?.pageSize ?? 10,
          );
          setModalData({
            modal: null,
            bannerMessage: null,
            apiErrorMessage: undefined,
          });
        })
        .catch((error) => {
          setModalData((prev) => ({
            ...prev,
            apiErrorMessage: error.response.data,
          }));
        });
    } else if (modalData.modal === Modal.Update) {
      bannerMessageService
        .Update(values)
        .then(() => {
          updateTable(
            requestData.pagination?.page ?? 1,
            requestData.pagination?.pageSize ?? 10,
          );
          setModalData({
            modal: null,
            bannerMessage: null,
            apiErrorMessage: undefined,
          });
        })
        .catch((error) => {
          setModalData((prev) => ({
            ...prev,
            apiErrorMessage: error.response.data,
          }));
        });
    }
  };

  return (
    <>
      <h4 className="text-black">Banner</h4>
      <ApiTable
        loading={loading}
        state={requestData}
        pageOptions={[10, 20, 50]}
        data={data}
        columns={cols}
        controls={controls()}
        onChange={(state) => setRequestData(state)}
      />
      <DeleteBannerMessageModal
        show={modalData.modal === Modal.Delete}
        id={modalData.bannerMessage?.id ?? 0}
        onDelete={onDelete}
        onHide={onHide}
      />
      <BannerMessageFormModal
        show={
          modalData.modal === Modal.Create || modalData.modal === Modal.Update
        }
        onClose={onHide}
        bannerMessage={modalData.bannerMessage}
        onSubmit={onSubmit}
        apiErrorMessage={modalData.apiErrorMessage}
      />
    </>
  );
};

export default Banner;
